/**
 * Variable Pill Editor Component
 * 
 * A contenteditable-based editor that renders {{variableKey}} tokens
 * as styled pills while maintaining a plain text bodyTemplate for storage.
 * Supports cursor-position-aware variable insertion.
 */

import React, { useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import { cn } from '@/lib/utils';
import { type PromptVariableDefinition } from '../../../../shared/promptVariables';

// Regex to match {{variableKey}} tokens
const VARIABLE_PATTERN = /(\{\{\w+\}\})/g;

interface VariablePillEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  variables: PromptVariableDefinition[];
  className?: string;
  minRows?: number;
  /** AR-210 (TC-38.1): id of the visible label for the editable region. */
  'aria-labelledby'?: string;
}

export interface VariablePillEditorRef {
  insertVariable: (key: string) => void;
}

/**
 * Parse plain text into segments of plain text and variable tokens
 */
function parseTemplate(template: string): Array<{ type: 'text' | 'variable'; content: string; key?: string }> {
  const segments: Array<{ type: 'text' | 'variable'; content: string; key?: string }> = [];
  const parts = template.split(VARIABLE_PATTERN);
  
  for (const part of parts) {
    if (!part) continue;
    
    if (part.match(/^\{\{\w+\}\}$/)) {
      const key = part.slice(2, -2);
      segments.push({ type: 'variable', content: part, key });
    } else {
      segments.push({ type: 'text', content: part });
    }
  }
  
  return segments;
}

/**
 * Extract plain text from contenteditable HTML
 */
function extractPlainText(element: HTMLElement): string {
  let result = '';
  
  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      result += node.textContent || '';
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      // Check if it's a variable pill
      const varKey = el.getAttribute('data-variable');
      if (varKey) {
        result += `{{${varKey}}}`;
      } else if (el.tagName === 'BR') {
        result += '\n';
      } else {
        result += extractPlainText(el);
      }
    }
  }
  
  return result;
}

/**
 * Save and restore cursor position
 */
function saveCursorPosition(element: HTMLElement): { node: Node; offset: number } | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  
  const range = selection.getRangeAt(0);
  if (!element.contains(range.startContainer)) return null;
  
  return { node: range.startContainer, offset: range.startOffset };
}

function restoreCursorPosition(element: HTMLElement, position: { node: Node; offset: number } | null) {
  if (!position) return;
  
  try {
    const selection = window.getSelection();
    if (!selection) return;
    
    const range = document.createRange();
    range.setStart(position.node, Math.min(position.offset, position.node.textContent?.length || 0));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  } catch {
    // Cursor restore failed, that's OK
  }
}

export const VariablePillEditor = forwardRef<VariablePillEditorRef, VariablePillEditorProps>(
  function VariablePillEditor(
    {
      value,
      onChange,
      placeholder = 'Enter your prompt template...',
      variables,
      className,
      minRows = 6,
      'aria-labelledby': ariaLabelledBy,
    },
    ref
  ) {
  const editorRef = useRef<HTMLDivElement>(null);
  const isUpdatingRef = useRef(false);
  
  // Create a map of variable keys to their definitions
  const variableMap = useMemo(() => {
    const map = new Map<string, PromptVariableDefinition>();
    for (const v of variables) {
      map.set(v.key, v);
    }
    return map;
  }, [variables]);
  
  /**
   * Render the template as HTML with variable pills
   */
  const renderHTML = useCallback((template: string): string => {
    if (!template) return '';
    
    const segments = parseTemplate(template);
    let html = '';
    
    for (const segment of segments) {
      if (segment.type === 'variable' && segment.key) {
        const varDef = variableMap.get(segment.key);
        const label = varDef?.label || segment.key;
        const isKnown = variableMap.has(segment.key);
        
        // Create a non-editable pill span
        html += `<span 
          contenteditable="false" 
          data-variable="${segment.key}" 
          class="variable-pill ${isKnown ? 'known' : 'unknown'}"
          title="${varDef?.description || segment.key}"
        >${label}</span>`;
      } else {
        // Escape HTML and convert newlines to <br>
        const escaped = segment.content
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/\n/g, '<br>');
        html += escaped;
      }
    }
    
    return html;
  }, [variableMap]);
  
  /**
   * Sync editor content when value changes externally
   */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || isUpdatingRef.current) return;
    
    const currentText = extractPlainText(editor);
    if (currentText !== value) {
      const html = renderHTML(value);
      editor.innerHTML = html || '';
    }
  }, [value, renderHTML]);
  
  /**
   * Initial render - ensure pills are shown on mount and when variables list changes
   */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || isUpdatingRef.current) return;
    
    // Always render with pills when variableMap changes (ensures initial render works)
    const html = renderHTML(value);
    editor.innerHTML = html || '';
  }, [variableMap]); // Re-render when variables are loaded
  
  /**
   * Handle input changes
   */
  const handleInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;
    
    isUpdatingRef.current = true;
    const plainText = extractPlainText(editor);
    onChange(plainText);
    isUpdatingRef.current = false;
  }, [onChange]);
  
  /**
   * Handle paste - convert to plain text
   */
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);
  
  /**
   * Insert a variable at the current cursor position
   */
  const insertVariable = useCallback((key: string) => {
    const editor = editorRef.current;
    if (!editor) return;
    
    editor.focus();
    
    // Get current plain text and find cursor position
    const selection = window.getSelection();
    let insertPosition = extractPlainText(editor).length; // Default: end
    
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      if (editor.contains(range.startContainer)) {
        // Calculate position in plain text
        const beforeRange = document.createRange();
        beforeRange.setStart(editor, 0);
        beforeRange.setEnd(range.startContainer, range.startOffset);
        
        // Create a temp element to extract text before cursor
        const temp = document.createElement('div');
        temp.appendChild(beforeRange.cloneContents());
        insertPosition = extractPlainText(temp).length;
      }
    }
    
    // Insert variable at the calculated position
    const currentText = extractPlainText(editor);
    const newText = currentText.slice(0, insertPosition) + `{{${key}}}` + currentText.slice(insertPosition);
    
    // Update value through onChange
    isUpdatingRef.current = true;
    onChange(newText);
    isUpdatingRef.current = false;
    
    // Re-render with pills and restore cursor after the new variable
    requestAnimationFrame(() => {
      const html = renderHTML(newText);
      editor.innerHTML = html || '';
      
      // Position cursor after the inserted variable
      // Find the pill we just inserted and place cursor after it
      const pills = editor.querySelectorAll(`[data-variable="${key}"]`);
      if (pills.length > 0) {
        const lastPill = pills[pills.length - 1];
        const newRange = document.createRange();
        newRange.setStartAfter(lastPill);
        newRange.collapse(true);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(newRange);
        }
      }
      editor.focus();
    });
  }, [onChange, renderHTML]);
  
  /**
   * Handle keydown for special behavior
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Allow normal text editing
    if (e.key === 'Backspace' || e.key === 'Delete') {
      // Let the browser handle deletion
      // The handleInput will sync the state
      setTimeout(handleInput, 0);
    }
  }, [handleInput]);
  
  // Expose insertVariable method via ref
  useImperativeHandle(ref, () => ({
    insertVariable,
  }), [insertVariable]);
  
  const minHeight = minRows * 1.5 * 16; // Approximate line height
  
  return (
    <div className={cn('variable-pill-editor-wrapper relative', className)}>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-labelledby={ariaLabelledBy}
        aria-placeholder={placeholder}
        className={cn(
          'variable-pill-editor',
          'w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'overflow-auto whitespace-pre-wrap break-words',
          !value && 'text-muted-foreground',
        )}
        style={{ minHeight }}
        onInput={handleInput}
        onPaste={handlePaste}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
      />
      {!value && (
        <div 
          className="absolute left-3 top-2 text-sm text-muted-foreground pointer-events-none"
          aria-hidden
        >
          {placeholder}
        </div>
      )}
      <style>{`
        .variable-pill-editor {
          min-height: ${minHeight}px;
        }
        .variable-pill-editor:empty:before {
          content: attr(data-placeholder);
          color: var(--muted-foreground);
        }
        .variable-pill {
          display: inline-flex;
          align-items: center;
          padding: 0 6px;
          margin: 0 2px;
          height: 22px;
          font-size: 12px;
          font-weight: 500;
          border-radius: 4px;
          cursor: default;
          user-select: none;
          vertical-align: baseline;
        }
        .variable-pill.known {
          background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%);
          color: white;
          box-shadow: 0 1px 2px rgba(168, 85, 247, 0.3);
        }
        .variable-pill.unknown {
          background: #fef2f2;
          color: #b91c1c;
          border: 1px solid #fca5a5;
        }
      `}</style>
    </div>
  );
});

export default VariablePillEditor;

